import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom"; // Import NavLink
import Swal from "sweetalert2"; // Import SweetAlert for Success/Error Popups

import {
  GraduationCap,
  MapPin,
  Menu,
  X,
  Globe,
  Phone,
  Mail,
  Clock,
  Send,
  MessageSquare,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  ChevronRight,
  ArrowRight,
  Loader2,
} from "lucide-react";

export default function ContactUsPage() {
  // --- UI STATES ---
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- FORM STATE ---
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "General Inquiry",
    message: "",
  });

  // Header Scroll Effect
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // --- HANDLERS ---
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Basic Validation
    if (
      !formData.firstName ||
      !formData.email ||
      !formData.phone ||
      !formData.message
    ) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please fill in all required fields.",
        confirmButtonColor: "#dc2626",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        "https://map-your-college.siteflow.in/admin/save_contact.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        },
      );

      const result = await response.json();

      if (result.status === "success") {
        Swal.fire({
          icon: "success",
          title: "Message Sent!",
          text: "We will get back to you shortly.",
          confirmButtonColor: "#dc2626",
        });
        // Reset Form
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          subject: "General Inquiry",
          message: "",
        });
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error("Submission Error:", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong. Please try again later.",
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 selection:bg-red-100 selection:text-red-900 overflow-x-hidden">
      {/* ================= HEADER (UPDATED WITH ACTIVE LINK) ================= */}
      <header
        className={`fixed w-full z-50 transition-all duration-300 border-b border-transparent ${
          isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-sm border-slate-100 py-3"
            : "bg-blue-950 py-5 border-blue-900"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* LOGO */}
            <Link
              to="/"
              className="flex items-center gap-3 cursor-pointer group"
            >
              <div className="relative">
                <GraduationCap
                  className={`w-10 h-10 ${isScrolled ? "text-blue-950" : "text-white"} transition-colors`}
                  strokeWidth={2}
                />
                <div className="absolute top-[18px] left-1/2 -translate-x-1/2">
                  <MapPin className="w-4 h-4 text-red-600 fill-red-600" />
                </div>
              </div>
              <div className="flex flex-col">
                <span
                  className={`text-2xl font-bold tracking-tight leading-none ${isScrolled ? "text-blue-950" : "text-white"}`}
                >
                  Map
                  <span
                    className={`${isScrolled ? "text-blue-800" : "text-blue-200"}`}
                  >
                    Your
                  </span>
                  College
                </span>
                <span className="text-[10px] font-bold tracking-widest text-slate-400 uppercase mt-1">
                  Nurturing Dreams, Creating Leaders
                </span>
              </div>
            </Link>

            {/* DESKTOP NAVIGATION */}
            <nav className="hidden lg:flex items-center gap-8">
              {[
                { name: "Home", path: "/" },
                { name: "Colleges", path: "/colleges" },
                { name: "About Us", path: "/about" },
                { name: "Services", path: "/services" },
                { name: "Contact", path: "/contact" },
              ].map((link) => (
                <NavLink
                  key={link.name}
                  to={link.path}
                  className={({ isActive }) =>
                    `text-sm font-semibold transition-colors uppercase tracking-wide hover:text-red-600 ${
                      isActive
                        ? "text-red-600" // Active State
                        : isScrolled
                          ? "text-slate-600"
                          : "text-blue-100" // Normal State
                    }`
                  }
                >
                  {link.name}
                </NavLink>
              ))}

              <Link to="/contact">
                <button className="bg-red-600 text-white px-6 py-2.5 rounded text-sm font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 transform hover:-translate-y-0.5">
                  Get Started
                </button>
              </Link>
            </nav>

            {/* MOBILE MENU BUTTON */}
            <button
              className={`${isScrolled ? "text-blue-950" : "text-white"} lg:hidden`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* MOBILE MENU DROPDOWN */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 w-full bg-white border-b border-slate-100 shadow-xl lg:hidden p-4 flex flex-col gap-4 animate-in slide-in-from-top-2">
            {[
              { name: "Home", path: "/" },
              { name: "Colleges", path: "/colleges" },
              { name: "About Us", path: "/about" },
              { name: "Services", path: "/services" },
              { name: "Contact", path: "/contact" },
            ].map((link) => (
              <NavLink
                key={link.name}
                to={link.path}
                className={({ isActive }) =>
                  `text-lg font-semibold py-2 border-b border-slate-50 ${
                    isActive ? "text-red-600" : "text-blue-950"
                  }`
                }
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </NavLink>
            ))}
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
              <button className="bg-red-600 text-white w-full py-3 rounded font-bold">
                Get Started
              </button>
            </Link>
          </div>
        )}
      </header>

      {/* ================= HERO SECTION ================= */}
      <section className="bg-blue-950 text-white pt-40 pb-20 relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">
            Get in Touch
          </h1>
          <p className="text-blue-200 text-lg max-w-2xl mx-auto">
            Have questions about admissions, colleges, or our services? Our
            expert counselors are here to help you map your future.
          </p>
        </div>
      </section>

      {/* ================= CONTACT CONTENT ================= */}
      <section className="py-20 px-4 -mt-10 relative z-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Side: Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-md border border-slate-100 flex items-start gap-4">
                <div className="bg-blue-50 p-3 rounded-lg text-blue-950">
                  <Phone size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-blue-950 mb-1">Call Us</h3>
                  <p className="text-slate-600 text-sm">+91 7771011010</p>
                </div>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md border border-slate-100 flex items-start gap-4">
                <div className="bg-red-50 p-3 rounded-lg text-red-600">
                  <Mail size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-blue-950 mb-1">Email Us</h3>
                  <p className="text-slate-600 text-sm">
                    info@mapyourcollege.com
                  </p>
                </div>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md border border-slate-100 flex items-start gap-4">
                <div className="bg-blue-50 p-3 rounded-lg text-blue-950">
                  <MapPin size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-blue-950 mb-1">Visit HQ</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    MZ-6 Dm tower Near Zanzeerwala Square, Indore
                  </p>
                </div>
              </div>
              <div className="pt-6">
                <h4 className="font-bold text-blue-950 mb-4">Follow Us</h4>
                <div className="flex gap-4">
                  {[Facebook, Twitter, Linkedin, Instagram].map((Icon, idx) => (
                    <a
                      key={idx}
                      href="#"
                      className="w-10 h-10 rounded-full bg-white shadow border border-slate-100 flex items-center justify-center text-slate-500 hover:bg-blue-950 hover:text-white transition-all"
                    >
                      <Icon size={18} />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Side: Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8 md:p-10">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-blue-950">
                    Send us a Message
                  </h2>
                  <p className="text-slate-500 mt-2">
                    Fill out the form below and our team will get back to you
                    within 24 hours.
                  </p>
                </div>

                <form className="space-y-6" onSubmit={handleFormSubmit}>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-blue-950 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="John"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:border-blue-950 outline-none transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-blue-950 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Doe"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:border-blue-950 outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-blue-950 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@example.com"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:border-blue-950 outline-none transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-blue-950 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+91 98765 43210"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:border-blue-950 outline-none transition-colors"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-blue-950 mb-2">
                      Subject
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:border-blue-950 outline-none transition-colors text-slate-600"
                    >
                      <option>General Inquiry</option>
                      <option>College Admission Help</option>
                      <option>Partner with us</option>
                      <option>Career Opportunities</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-blue-950 mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Tell us how we can help you..."
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:border-blue-950 outline-none transition-colors"
                      required
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-600 text-white font-bold py-4 rounded-lg hover:bg-red-700 transition-colors shadow-lg shadow-red-200 flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        {" "}
                        <Loader2 className="animate-spin" /> Sending...{" "}
                      </>
                    ) : (
                      <>
                        {" "}
                        <Send size={18} /> Send Message{" "}
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= MAP SECTION ================= */}
      <section className="h-96 w-full bg-slate-200 relative">
        <iframe
          src="https://www.google.com/maps?q=MZ-6+DM+Tower+Near+Zanzeerwala+Square+Indore&output=embed"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="filter grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-500"
        ></iframe>

        <div className="absolute bottom-6 left-6 md:left-20 bg-white p-4 rounded-lg shadow-lg max-w-xs hidden md:block">
          <h4 className="font-bold text-blue-950 flex items-center gap-2">
            <MapPin size={16} className="text-red-600" /> Headquarters
          </h4>
          <p className="text-xs text-slate-500 mt-1">
            MZ-6 DM Tower, Near Zanzeerwala Square, Indore
          </p>

          <a
            href="https://www.google.com/maps/search/?api=1&query=MZ-6+DM+Tower+Near+Zanzeerwala+Square+Indore"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 font-bold mt-2 flex items-center gap-1 hover:underline"
          >
            Get Directions <ArrowRight size={12} />
          </a>
        </div>
      </section>
    </div>
  );
}
